import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Autocomplete,
} from '@mui/material';
import {
  Search as SearchIcon,
  History as HistoryIcon,
  SwapHoriz as SwapIcon,
  Info as InfoIcon,
  Add as AddIcon,
  Power as ChargerIcon,
  Inventory as InventoryIcon,
  Build as BuildIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  ErrorOutline as ErrorIcon,
  Block as RetiredIcon,
} from '@mui/icons-material';
import { ChargerService, Charger, ChargerStatus, ChargerType, ChargerSwapHistoryRecord, ChargerHealthStats } from '../services/chargerService';
import { ChargerStatusChip } from '../components/ChargerStatusChip';
import { ChargerTypeChip } from '../components/ChargerTypeChip';
import { ChargerHealthIndicator } from '../components/ChargerHealthIndicator';

// Charger Details Dialog
const ChargerDetailsDialog: React.FC<{
  open: boolean;
  charger: Charger | null;
  onClose: () => void;
}> = ({ open, charger, onClose }) => {
  const [swapHistory, setSwapHistory] = useState<ChargerSwapHistoryRecord[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (charger?.id && open) {
      loadSwapHistory();
    }
  }, [charger?.id, open]);

  const loadSwapHistory = async () => {
    if (!charger?.id) return;
    setLoading(true);
    try {
      const history = await ChargerService.getChargerSwapHistory(charger.id);
      setSwapHistory(history);
    } catch (error) {
      console.error('Failed to load swap history:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!charger) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ChargerIcon />
          <Typography variant="h6">Charger Details - {charger.chargerNumber}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Basic Information
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Charger Number</Typography>
            <Typography variant="body1"><strong>{charger.chargerNumber}</strong></Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Status</Typography>
            <ChargerStatusChip status={charger.status} size="medium" />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Manufacturer</Typography>
            <Typography variant="body1">{charger.manufacturer}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Type</Typography>
            <ChargerTypeChip type={charger.chargerType} size="medium" />
          </Grid>

          {/* Technical Specifications */}
          <Grid item xs={12} mt={2}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Technical Specifications
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Power Output</Typography>
            <Typography variant="body1">{charger.powerOutput} kW</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Efficiency</Typography>
            <Typography variant="body1">{charger.efficiency ? `${charger.efficiency}%` : 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Input Voltage</Typography>
            <Typography variant="body1">{charger.inputVoltage || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Output Voltage</Typography>
            <Typography variant="body1">{charger.outputVoltage || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Connector Type</Typography>
            <Typography variant="body1">{charger.connectorType || 'N/A'}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Cable Length</Typography>
            <Typography variant="body1">{charger.cableLength ? `${charger.cableLength}m` : 'N/A'}</Typography>
          </Grid>

          {/* Health & Usage */}
          <Grid item xs={12} mt={2}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Health & Usage
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <ChargerHealthIndicator healthPercentage={charger.healthPercentage} showLabel size="large" />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Charging Cycles</Typography>
            <Typography variant="body1">{charger.chargingCycles.toLocaleString()}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Last Used</Typography>
            <Typography variant="body1">
              {charger.lastUsedDate ? new Date(charger.lastUsedDate).toLocaleDateString() : 'Never'}
            </Typography>
          </Grid>

          {/* Vehicle Assignment */}
          <Grid item xs={12} mt={2}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Vehicle Assignment
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Parent Vehicle</Typography>
            <Typography variant="body1">
              {charger.parentVehicle?.registrationNumber || 'N/A'}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="textSecondary">Current Vehicle</Typography>
            <Typography variant="body1">
              {charger.currentVehicle?.registrationNumber || 'In Storage'}
            </Typography>
          </Grid>

          {/* Warranty & Purchase */}
          {(charger.purchaseDate || charger.warrantyStartDate) && (
            <>
              <Grid item xs={12} mt={2}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Warranty & Purchase
                </Typography>
              </Grid>
              {charger.purchaseDate && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Purchase Date</Typography>
                  <Typography variant="body1">{new Date(charger.purchaseDate).toLocaleDateString()}</Typography>
                </Grid>
              )}
              {charger.warrantyStartDate && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Warranty Start</Typography>
                  <Typography variant="body1">{new Date(charger.warrantyStartDate).toLocaleDateString()}</Typography>
                </Grid>
              )}
              {charger.warrantyEndDate && (
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Warranty End</Typography>
                  <Typography variant="body1">{new Date(charger.warrantyEndDate).toLocaleDateString()}</Typography>
                </Grid>
              )}
            </>
          )}

          {/* Notes */}
          {charger.notes && (
            <>
              <Grid item xs={12} mt={2}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Notes
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2">{charger.notes}</Typography>
              </Grid>
            </>
          )}

          {/* Swap History */}
          <Grid item xs={12} mt={2}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Swap History ({swapHistory.length})
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : swapHistory.length === 0 ? (
              <Alert severity="info">No swap history available</Alert>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>From Vehicle</TableCell>
                      <TableCell>To Vehicle</TableCell>
                      <TableCell>Reason</TableCell>
                      <TableCell>Performed By</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {swapHistory.map((swap) => (
                      <TableRow key={swap.id}>
                        <TableCell>{new Date(swap.swapDate).toLocaleString()}</TableCell>
                        <TableCell>{swap.fromVehicle?.registrationNumber || 'Storage'}</TableCell>
                        <TableCell>{swap.toVehicle?.registrationNumber || 'Storage'}</TableCell>
                        <TableCell>{swap.swapReason}</TableCell>
                        <TableCell>{swap.performedBy}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Vehicle option type for autocomplete
interface VehicleOption {
  id: string;
  registrationNumber: string;
  model?: {
    name: string;
  };
}

// Charger Swap Dialog Component
const ChargerSwapDialog: React.FC<{
  open: boolean;
  charger: Charger | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ open, charger, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleOption | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<VehicleOption[]>([]);
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedVehicle(null);
      setInputValue('');
      setOptions([]);
      setReason('');
      setRemarks('');
      setError('');
    }
  }, [open]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const searchVehicles = async (searchValue: string) => {
      setLoadingVehicles(true);
      try {
        const apiUrl = import.meta.env.VITE_API_GATEWAY_URL || 'https://testingdb.ev91riders.com';
        const response = await fetch(`${apiUrl}/api/vehicles?search=${encodeURIComponent(searchValue)}&limit=50`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success && data.vehicles) {
          setOptions(data.vehicles);
          if (data.vehicles.length === 1 && data.vehicles[0].registrationNumber === searchValue) {
            setSelectedVehicle(data.vehicles[0]);
          }
        } else if (data.success && data.data) {
          const vehiclesList = data.data.vehicles || data.data;
          setOptions(vehiclesList);
          if (vehiclesList.length === 1 && vehiclesList[0].registrationNumber === searchValue) {
            setSelectedVehicle(vehiclesList[0]);
          }
        } else {
          setOptions([]);
        }
      } catch (error: any) {
        console.error('❌ Failed to search vehicles:', error);
        setOptions([]);
      } finally {
        setLoadingVehicles(false);
      }
    };

    if (inputValue.length >= 2 && !selectedVehicle) {
      timeoutId = setTimeout(() => {
        searchVehicles(inputValue);
      }, 300);
    } else if (inputValue.length < 2 && !selectedVehicle) {
      setOptions([]);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [inputValue, selectedVehicle]);

  const handlePaste = (event: React.ClipboardEvent) => {
    const pastedText = event.clipboardData.getData('text').trim();
    if (pastedText.length >= 2) {
      setInputValue(pastedText);
      searchVehiclesImmediately(pastedText);
    }
  };

  const searchVehiclesImmediately = async (searchValue: string) => {
    setLoadingVehicles(true);
    try {
      const apiUrl = import.meta.env.VITE_API_GATEWAY_URL || 'https://testingdb.ev91riders.com';
      const response = await fetch(`${apiUrl}/api/vehicles?search=${encodeURIComponent(searchValue)}&limit=50`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.vehicles) {
        setOptions(data.vehicles);
        if (data.vehicles.length === 1 && data.vehicles[0].registrationNumber === searchValue) {
          setSelectedVehicle(data.vehicles[0]);
        }
      } else if (data.success && data.data) {
        const vehiclesList = data.data.vehicles || data.data;
        setOptions(vehiclesList);
        if (vehiclesList.length === 1 && vehiclesList[0].registrationNumber === searchValue) {
          setSelectedVehicle(vehiclesList[0]);
        }
      } else {
        setOptions([]);
      }
    } catch (error: any) {
      console.error('❌ Failed to search vehicles:', error);
      setOptions([]);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const handleSwap = async () => {
    if (!charger) return;

    if (!reason.trim()) {
      setError('Swap reason is required');
      return;
    }

    const currentUser = localStorage.getItem('userName') || 'Unknown User';

    setLoading(true);
    setError('');

    try {
      await ChargerService.swapCharger({
        chargerId: charger.id,
        fromVehicleId: charger.currentVehicleId || undefined,
        toVehicleId: selectedVehicle?.id || undefined,
        swapReason: reason,
        performedBy: currentUser,
        remarks: remarks || undefined,
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Charger swap error:', err);
      setError(err.response?.data?.message || 'Failed to swap charger');
    } finally {
      setLoading(false);
    }
  };

  if (!charger) return null;

  const isToStorage = !selectedVehicle;
  const swapType = charger.currentVehicleId
    ? (isToStorage ? 'Vehicle to Storage' : 'Vehicle to Vehicle')
    : 'Storage to Vehicle';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SwapIcon color="primary" />
          <Typography variant="h6">Swap Charger</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Charger Info */}
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                Charger Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Charger Number</Typography>
                  <Typography variant="body1"><strong>{charger.chargerNumber}</strong></Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Current Location</Typography>
                  <Typography variant="body1">
                    {charger.currentVehicle?.registrationNumber || 'Storage'}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">Swap Type</Typography>
                  <Typography variant="body1" color="primary"><strong>{swapType}</strong></Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Vehicle Selection */}
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              freeSolo
              clearOnBlur={false}
              blurOnSelect={false}
              options={options}
              value={selectedVehicle}
              onChange={(_, newValue) => {
                if (typeof newValue !== 'string') {
                  setSelectedVehicle(newValue);
                }
              }}
              onInputChange={(_, newInputValue) => {
                setInputValue(newInputValue);
              }}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : `${option.registrationNumber} - ${option.model?.name || 'Unknown'}`
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              loading={loadingVehicles}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Target Vehicle (Leave empty for storage)"
                  placeholder="Search by registration number..."
                  onPaste={handlePaste}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingVehicles ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  helperText="Type 2+ characters or paste registration number. Leave empty to move to storage."
                />
              )}
            />
          </FormControl>

          {/* Swap Reason */}
          <TextField
            fullWidth
            required
            label="Swap Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Scheduled maintenance, Charger upgrade, etc."
            error={!!error && !reason.trim()}
          />

          {/* Remarks */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Remarks (Optional)"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            placeholder="Additional notes about this swap..."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSwap}
          variant="contained"
          color="primary"
          disabled={loading || !reason.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <SwapIcon />}
        >
          {loading ? 'Swapping...' : 'Swap Charger'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Add/Create Charger Dialog Component
const CreateChargerDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [error, setError] = useState('');
  const [parentInputValue, setParentInputValue] = useState('');
  const [currentInputValue, setCurrentInputValue] = useState('');
  const [parentOptions, setParentOptions] = useState<any[]>([]);
  const [currentOptions, setCurrentOptions] = useState<any[]>([]);

  const [formData, setFormData] = useState({
    chargerNumber: '',
    manufacturer: '',
    chargerType: ChargerType.PORTABLE_AC_SINGLE_PHASE,
    powerOutput: '',
    inputVoltage: '',
    outputVoltage: '',
    efficiency: '',
    cableLength: '',
    connectorType: '',
    parentVehicleId: '',
    currentVehicleId: '',
    status: ChargerStatus.IN_STORAGE,
    healthPercentage: '100',
    chargingCycles: '0',
    warrantyStartDate: '',
    warrantyEndDate: '',
    purchaseDate: '',
    notes: '',
  });

  useEffect(() => {
    if (open) {
      // Reset form
      setFormData({
        chargerNumber: '',
        manufacturer: '',
        chargerType: ChargerType.PORTABLE_AC_SINGLE_PHASE,
        powerOutput: '',
        inputVoltage: '',
        outputVoltage: '',
        efficiency: '',
        cableLength: '',
        connectorType: '',
        parentVehicleId: '',
        currentVehicleId: '',
        status: ChargerStatus.IN_STORAGE,
        healthPercentage: '100',
        chargingCycles: '0',
        warrantyStartDate: '',
        warrantyEndDate: '',
        purchaseDate: '',
        notes: '',
      });
      setError('');
      setParentInputValue('');
      setCurrentInputValue('');
      setParentOptions([]);
      setCurrentOptions([]);
    }
  }, [open]);

  // Search for parent vehicle
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const searchVehicles = async (searchValue: string) => {
      setLoadingVehicles(true);
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://testingdb.ev91riders.com/api';
        const response = await fetch(`${apiUrl}/vehicles?search=${encodeURIComponent(searchValue)}&limit=50`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });
        const data = await response.json();
        if (data.success && data.vehicles) {
          setParentOptions(data.vehicles);
        } else {
          setParentOptions([]);
        }
      } catch (err) {
        console.error('Failed to search vehicles:', err);
        setParentOptions([]);
      } finally {
        setLoadingVehicles(false);
      }
    };

    if (parentInputValue.length >= 2 && !formData.parentVehicleId) {
      timeoutId = setTimeout(() => {
        searchVehicles(parentInputValue);
      }, 300);
    } else if (parentInputValue.length < 2 && !formData.parentVehicleId) {
      setParentOptions([]);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [parentInputValue, formData.parentVehicleId]);

  // Search for current vehicle
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const searchVehicles = async (searchValue: string) => {
      setLoadingVehicles(true);
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'https://testingdb.ev91riders.com/api';
        const response = await fetch(`${apiUrl}/vehicles?search=${encodeURIComponent(searchValue)}&limit=50`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });
        const data = await response.json();
        if (data.success && data.vehicles) {
          setCurrentOptions(data.vehicles);
        } else {
          setCurrentOptions([]);
        }
      } catch (err) {
        console.error('Failed to search vehicles:', err);
        setCurrentOptions([]);
      } finally {
        setLoadingVehicles(false);
      }
    };

    if (currentInputValue.length >= 2 && !formData.currentVehicleId) {
      timeoutId = setTimeout(() => {
        searchVehicles(currentInputValue);
      }, 300);
    } else if (currentInputValue.length < 2 && !formData.currentVehicleId) {
      setCurrentOptions([]);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [currentInputValue, formData.currentVehicleId]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.chargerNumber.trim()) {
      setError('Charger number is required');
      return;
    }
    if (!formData.manufacturer.trim()) {
      setError('Manufacturer is required');
      return;
    }
    if (!formData.powerOutput || parseFloat(formData.powerOutput) <= 0) {
      setError('Valid power output is required');
      return;
    }
    if (!formData.parentVehicleId) {
      setError('Parent vehicle is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const payload: any = {
        chargerNumber: formData.chargerNumber.trim(),
        manufacturer: formData.manufacturer.trim(),
        chargerType: formData.chargerType,
        powerOutput: parseFloat(formData.powerOutput),
        parentVehicleId: formData.parentVehicleId,
        status: formData.status,
        healthPercentage: parseFloat(formData.healthPercentage) || 100,
        chargingCycles: parseInt(formData.chargingCycles) || 0,
      };

      // Add optional fields
      if (formData.currentVehicleId) payload.currentVehicleId = formData.currentVehicleId;
      if (formData.inputVoltage) payload.inputVoltage = formData.inputVoltage.trim();
      if (formData.outputVoltage) payload.outputVoltage = formData.outputVoltage.trim();
      if (formData.efficiency) payload.efficiency = parseFloat(formData.efficiency);
      if (formData.cableLength) payload.cableLength = parseFloat(formData.cableLength);
      if (formData.connectorType) payload.connectorType = formData.connectorType.trim();
      if (formData.warrantyStartDate) payload.warrantyStartDate = formData.warrantyStartDate;
      if (formData.warrantyEndDate) payload.warrantyEndDate = formData.warrantyEndDate;
      if (formData.purchaseDate) payload.purchaseDate = formData.purchaseDate;
      if (formData.notes) payload.notes = formData.notes.trim();

      await ChargerService.createCharger(payload);
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create charger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Charger</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={3}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Basic Information */}
          <Typography variant="subtitle2" color="primary">Basic Information</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Charger Number"
                value={formData.chargerNumber}
                onChange={(e) => handleChange('chargerNumber', e.target.value)}
                placeholder="e.g., CHG-001-2024"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Manufacturer"
                value={formData.manufacturer}
                onChange={(e) => handleChange('manufacturer', e.target.value)}
                placeholder="e.g., Delta, ABB"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Charger Type</InputLabel>
                <Select
                  value={formData.chargerType}
                  onChange={(e) => handleChange('chargerType', e.target.value)}
                  label="Charger Type"
                >
                  <MenuItem value={ChargerType.PORTABLE_AC_SINGLE_PHASE}>Portable AC (1φ)</MenuItem>
                  <MenuItem value={ChargerType.PORTABLE_AC_THREE_PHASE}>Portable AC (3φ)</MenuItem>
                  <MenuItem value={ChargerType.PORTABLE_DC_FAST}>Portable DC Fast</MenuItem>
                  <MenuItem value={ChargerType.WALL_MOUNTED_AC}>Wall-Mounted AC</MenuItem>
                  <MenuItem value={ChargerType.WALL_MOUNTED_DC}>Wall-Mounted DC</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value={ChargerStatus.ACTIVE}>Active</MenuItem>
                  <MenuItem value={ChargerStatus.IN_STORAGE}>In Storage</MenuItem>
                  <MenuItem value={ChargerStatus.UNDER_REPAIR}>Under Repair</MenuItem>
                  <MenuItem value={ChargerStatus.DAMAGED}>Damaged</MenuItem>
                  <MenuItem value={ChargerStatus.RETIRED}>Retired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {/* Technical Specifications */}
          <Typography variant="subtitle2" color="primary">Technical Specifications</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                required
                type="number"
                label="Power Output (kW)"
                value={formData.powerOutput}
                onChange={(e) => handleChange('powerOutput', e.target.value)}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Input Voltage"
                value={formData.inputVoltage}
                onChange={(e) => handleChange('inputVoltage', e.target.value)}
                placeholder="e.g., 230V AC"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Output Voltage"
                value={formData.outputVoltage}
                onChange={(e) => handleChange('outputVoltage', e.target.value)}
                placeholder="e.g., 48V DC"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Efficiency (%)"
                value={formData.efficiency}
                onChange={(e) => handleChange('efficiency', e.target.value)}
                InputProps={{ inputProps: { min: 0, max: 100, step: 0.1 } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Cable Length (m)"
                value={formData.cableLength}
                onChange={(e) => handleChange('cableLength', e.target.value)}
                InputProps={{ inputProps: { min: 0, step: 0.1 } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Connector Type"
                value={formData.connectorType}
                onChange={(e) => handleChange('connectorType', e.target.value)}
                placeholder="e.g., CCS2, CHAdeMO"
              />
            </Grid>
          </Grid>

          {/* Vehicle Assignment */}
          <Typography variant="subtitle2" color="primary">Vehicle Assignment</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                freeSolo
                clearOnBlur={false}
                blurOnSelect={false}
                options={parentOptions}
                value={parentOptions.find((v: any) => v.id === formData.parentVehicleId) || null}
                onChange={(_, newValue) => {
                  if (typeof newValue !== 'string') {
                    handleChange('parentVehicleId', newValue?.id || '');
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  setParentInputValue(newInputValue);
                }}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : `${option.registrationNumber} - ${option.model?.name || 'Unknown'}`
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={loadingVehicles}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Parent Vehicle *"
                    required
                    placeholder="Search by registration number..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingVehicles ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    helperText="Type 2+ characters to search for parent vehicle"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                freeSolo
                clearOnBlur={false}
                blurOnSelect={false}
                options={currentOptions}
                value={formData.currentVehicleId ? currentOptions.find((v: any) => v.id === formData.currentVehicleId) || null : null}
                onChange={(_, newValue) => {
                  if (typeof newValue !== 'string') {
                    handleChange('currentVehicleId', newValue?.id || '');
                  }
                }}
                onInputChange={(_, newInputValue) => {
                  setCurrentInputValue(newInputValue);
                }}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : `${option.registrationNumber} - ${option.model?.name || 'Unknown'}`
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={loadingVehicles}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Current Vehicle (Optional)"
                    placeholder="Search by registration number..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingVehicles ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    helperText="Type 2+ characters or leave empty for storage"
                  />
                )}
              />
            </Grid>
          </Grid>

          {/* Health & Usage */}
          <Typography variant="subtitle2" color="primary">Health & Usage</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Health Percentage"
                value={formData.healthPercentage}
                onChange={(e) => handleChange('healthPercentage', e.target.value)}
                InputProps={{ inputProps: { min: 0, max: 100 } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Charging Cycles"
                value={formData.chargingCycles}
                onChange={(e) => handleChange('chargingCycles', e.target.value)}
                InputProps={{ inputProps: { min: 0 } }}
              />
            </Grid>
          </Grid>

          {/* Warranty & Purchase */}
          <Typography variant="subtitle2" color="primary">Warranty & Purchase</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Purchase Date"
                value={formData.purchaseDate}
                onChange={(e) => handleChange('purchaseDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Warranty Start Date"
                value={formData.warrantyStartDate}
                onChange={(e) => handleChange('warrantyStartDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="date"
                label="Warranty End Date"
                value={formData.warrantyEndDate}
                onChange={(e) => handleChange('warrantyEndDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {/* Notes */}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            placeholder="Additional notes or comments..."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Create Charger'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Update Charger Status Dialog Component
const UpdateChargerStatusDialog: React.FC<{
  open: boolean;
  charger: Charger | null;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ open, charger, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<ChargerStatus | ''>('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && charger) {
      setStatus(charger.status);
      setError('');
    }
  }, [open, charger]);

  const handleUpdateStatus = async () => {
    if (!charger || !status) {
      setError('Status is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await ChargerService.updateChargerStatus(charger.chargerNumber, status as ChargerStatus);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error updating charger status:', err);
      setError(err.response?.data?.message || 'Failed to update charger status');
    } finally {
      setLoading(false);
    }
  };

  if (!charger) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Update Charger Status
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="textSecondary">
              Charger Number
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {charger.chargerNumber}
            </Typography>
          </Box>

          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Current Status
            </Typography>
            <ChargerStatusChip status={charger.status} />
          </Box>

          <FormControl fullWidth>
            <InputLabel>New Status</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as ChargerStatus)}
              label="New Status"
              disabled={loading}
            >
              {/* <MenuItem value={ChargerStatus.ACTIVE}>
                <Box display="flex" alignItems="center" gap={1}>
                  ✓ Active
                </Box>
              </MenuItem>
              <MenuItem value={ChargerStatus.IN_STORAGE}>
                <Box display="flex" alignItems="center" gap={1}>
                  📦 In Storage
                </Box>
              </MenuItem> */}
              <MenuItem value={ChargerStatus.UNDER_REPAIR}>
                <Box display="flex" alignItems="center" gap={1}>
                  🔧 Under Repair
                </Box>
              </MenuItem>
              <MenuItem value={ChargerStatus.DAMAGED}>
                <Box display="flex" alignItems="center" gap={1}>
                  ⚠️ Damaged
                </Box>
              </MenuItem>
              <MenuItem value={ChargerStatus.RETIRED}>
                <Box display="flex" alignItems="center" gap={1}>
                  ❌ Retired
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          {error && (
            <Alert severity="error">{error}</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdateStatus}
          variant="contained"
          color="primary"
          disabled={loading || !status || status === charger.status}
        >
          {loading ? <CircularProgress size={24} /> : 'Update Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main Charger Inventory Component
const ChargerInventory: React.FC = () => {
  const [chargers, setChargers] = useState<Charger[]>([]);
  const [filteredChargers, setFilteredChargers] = useState<Charger[]>([]);
  const [healthStats, setHealthStats] = useState<ChargerHealthStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedCharger, setSelectedCharger] = useState<Charger | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [statusUpdateDialogOpen, setStatusUpdateDialogOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadChargers();
    loadHealthStats();
  }, []);

  useEffect(() => {
    filterChargers();
  }, [chargers, searchTerm, statusFilter, typeFilter]);

  const loadChargers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await ChargerService.getAllChargers();
      setChargers(data);
    } catch (err) {
      console.error('Failed to load chargers:', err);
      setError('Failed to load chargers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadHealthStats = async () => {
    try {
      const stats = await ChargerService.getChargerHealthStats();
      setHealthStats(stats);
    } catch (err) {
      console.error('Failed to load health stats:', err);
    }
  };

  const handleCreateSuccess = () => {
    loadChargers();
    loadHealthStats();
  };

  const filterChargers = () => {
    let filtered = [...chargers];

    // Search filter
   if (searchTerm) {
  const search = searchTerm.toLowerCase();

  filtered = filtered.filter((charger) =>
    (charger.chargerNumber ?? "")
      .toLowerCase()
      .includes(search) ||

    (charger.manufacturer ?? "")
      .toLowerCase()
      .includes(search) ||

    (charger.currentVehicle?.registrationNumber ?? "")
      .toLowerCase()
      .includes(search) ||

    (charger.parentVehicle?.registrationNumber ?? "")
      .toLowerCase()
      .includes(search)
  );
}


    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((charger) => charger.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter((charger) => charger.chargerType === typeFilter);
    }

    setFilteredChargers(filtered);
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = (charger: Charger) => {
    setSelectedCharger(charger);
    setDetailsDialogOpen(true);
  };

  const handleSwapCharger = (charger: Charger) => {
    setSelectedCharger(charger);
    setSwapDialogOpen(true);
  };

  const handleSwapSuccess = () => {
    loadChargers();
    setSwapDialogOpen(false);
  };

  const handleUpdateChargerStatus = (charger: Charger) => {
    setSelectedCharger(charger);
    setStatusUpdateDialogOpen(true);
  };

  const handleStatusUpdateSuccess = () => {
    loadChargers();
    loadHealthStats();
    setStatusUpdateDialogOpen(false);
  };

  const paginatedChargers = filteredChargers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculate status counts from chargers list
  const chargerStatusCounts = {
    underRepair: chargers.filter(c => c.status === ChargerStatus.UNDER_REPAIR).length,
    damaged: chargers.filter(c => c.status === ChargerStatus.DAMAGED).length,
    retired: chargers.filter(c => c.status === ChargerStatus.RETIRED).length,
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h4">Charger Inventory</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          color="primary"
          onClick={() => setCreateDialogOpen(true)}
        >
          Add Charger
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Health Stats Cards */}
      {healthStats && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <InventoryIcon color="primary" />
                  <Typography variant="h6">{healthStats.total}</Typography>
                </Box>
                <Typography color="textSecondary">Total Chargers</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <ChargerIcon color="success" />
                  <Typography variant="h6">{healthStats.active}</Typography>
                </Box>
                <Typography color="textSecondary">Active</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <BuildIcon color="action" />
                  <Typography variant="h6">{healthStats.inStorage}</Typography>
                </Box>
                <Typography color="textSecondary">In Storage</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <WarningIcon color="warning" />
                  <Typography variant="h6">{chargerStatusCounts.underRepair}</Typography>
                </Box>
                <Typography color="textSecondary">Under Repair</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <ErrorIcon color="error" />
                  <Typography variant="h6">{chargerStatusCounts.damaged}</Typography>
                </Box>
                <Typography color="textSecondary">Damaged</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1}>
                  <RetiredIcon sx={{ color: 'text.secondary' }} />
                  <Typography variant="h6">{chargerStatusCounts.retired}</Typography>
                </Box>
                <Typography color="textSecondary">Retired</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by charger number, manufacturer, vehicle..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value={ChargerStatus.ACTIVE}>Active</MenuItem>
                <MenuItem value={ChargerStatus.IN_STORAGE}>In Storage</MenuItem>
                <MenuItem value={ChargerStatus.UNDER_REPAIR}>Under Repair</MenuItem>
                <MenuItem value={ChargerStatus.DAMAGED}>Damaged</MenuItem>
                <MenuItem value={ChargerStatus.RETIRED}>Retired</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                label="Type"
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value={ChargerType.PORTABLE_AC_SINGLE_PHASE}>Portable AC (1φ)</MenuItem>
                <MenuItem value={ChargerType.PORTABLE_AC_THREE_PHASE}>Portable AC (3φ)</MenuItem>
                <MenuItem value={ChargerType.PORTABLE_DC_FAST}>Portable DC Fast</MenuItem>
                <MenuItem value={ChargerType.WALL_MOUNTED_AC}>Wall-Mounted AC</MenuItem>
                <MenuItem value={ChargerType.WALL_MOUNTED_DC}>Wall-Mounted DC</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="textSecondary">
              {filteredChargers.length} charger{filteredChargers.length !== 1 ? 's' : ''} found
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Chargers Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Charger Number</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Manufacturer</TableCell>
              <TableCell>Power (kW)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Parent Vehicle</TableCell>
              <TableCell>Current Vehicle</TableCell>
              <TableCell>Cycles</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : paginatedChargers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography color="textSecondary">No chargers found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedChargers.map((charger) => (
                <TableRow key={charger.id} hover>
                  <TableCell>
                    <strong>{charger.chargerNumber}</strong>
                  </TableCell>
                  <TableCell>
                    <ChargerTypeChip type={charger.chargerType} showIcon={false} />
                  </TableCell>
                  <TableCell>{charger.manufacturer}</TableCell>
                  <TableCell>{charger.powerOutput}</TableCell>
                  <TableCell>
                    <ChargerStatusChip status={charger.status} />
                  </TableCell>
                  <TableCell>
                    {charger.parentVehicle?.registrationNumber || (
                      <Typography variant="body2" color="textSecondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {charger.currentVehicle?.registrationNumber || (
                      <Typography variant="body2" color="textSecondary">In Storage</Typography>
                    )}
                  </TableCell>
                  <TableCell>{charger.chargingCycles.toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewDetails(charger)}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Swap History">
                        <IconButton size="small" onClick={() => handleViewDetails(charger)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Update Status">
                        <IconButton size="small" color="warning" onClick={() => handleUpdateChargerStatus(charger)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Swap Charger">
                        <IconButton size="small" color="primary" onClick={() => handleSwapCharger(charger)}>
                          <SwapIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredChargers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      {/* Create Charger Dialog */}
      <CreateChargerDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Charger Details Dialog */}
      <ChargerDetailsDialog
        open={detailsDialogOpen}
        charger={selectedCharger}
        onClose={() => setDetailsDialogOpen(false)}
      />

      {/* Charger Swap Dialog */}
      <ChargerSwapDialog
        open={swapDialogOpen}
        charger={selectedCharger}
        onClose={() => setSwapDialogOpen(false)}
        onSuccess={handleSwapSuccess}
      />

      {/* Update Charger Status Dialog */}
      <UpdateChargerStatusDialog
        open={statusUpdateDialogOpen}
        charger={selectedCharger}
        onClose={() => setStatusUpdateDialogOpen(false)}
        onSuccess={handleStatusUpdateSuccess}
      />
    </Box>
  );
};

export default ChargerInventory;